import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PropertyStatus, UserRole } from '@shared/schema';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { apiRequest } from '@/lib/api';
import { Link } from 'wouter';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Check, 
  X, 
  Eye, 
  Trash2, 
  Search, 
  MapPin, 
  Building2,
  CheckCircle,
  XCircle,
  Clock,
  Pencil,
  Copy,
  User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';

export default function AdminPropertiesPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [propertyToReject, setPropertyToReject] = useState<any>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Fetch all properties
  const { data: properties = [], isLoading } = useQuery({
    queryKey: ['admin/properties'],
    queryFn: async () => {
      console.log("Admin: Fetching all properties with auth, user:", user);
      const data = await apiRequest('GET', 'admin/properties');
      console.log("Admin: Properties data received:", data);
      return data;
    },
    enabled: !!user && (user.role === UserRole.ADMIN),
  });
  
  // Approve property mutation
  const approveMutation = useMutation({
    mutationFn: async (propertyId: number) => {
      const res = await apiRequest('PATCH', `/admin/properties/${propertyId}/approve`, {});
      // return res.json();
      return res;
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Property approved successfully',
      });
      queryClient.invalidateQueries({ queryKey: ['admin/properties'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: `Failed to approve property: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
  
  // Reject property mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ propertyId, reason }: { propertyId: number; reason: string }) => {
      const res = await apiRequest('PATCH', `/admin/properties/${propertyId}/reject`, { rejectionReason: reason });
      // return res.json();
      return res;
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Property rejected successfully',
      });
      setRejectDialogOpen(false);
      setRejectionReason('');
      queryClient.invalidateQueries({ queryKey: ['admin/properties'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: `Failed to reject property: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
  
  const handleApprove = (propertyId: number) => {
    if (confirm('Are you sure you want to approve this property?')) {
      approveMutation.mutate(propertyId);
    }
  };
  
  const handleRejectClick = (property: any) => {
    setPropertyToReject(property);
    setRejectDialogOpen(true);
  };
  
  const handleRejectConfirm = () => {
    if (!propertyToReject) return;
    
    if (!rejectionReason.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please provide a rejection reason',
        variant: 'destructive',
      });
      return;
    }
    
    rejectMutation.mutate({
      propertyId: propertyToReject.id,
      reason: rejectionReason
    });
  };
  
  // Filter properties based on search query and status filter
  const filteredProperties = properties.filter((property: any) => {
    const matchesSearch = 
      property.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      property.area.toLowerCase().includes(searchQuery.toLowerCase()) ||
      property.address.toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesFilter = 
      statusFilter === 'all' || 
      property.status === statusFilter;
      
    return matchesSearch && matchesFilter;
  });
  
  // Status badge component
  const getStatusBadge = (status: PropertyStatus) => {
    switch (status) {
      case PropertyStatus.ACTIVE:
        return <Badge className="bg-green-500 hover:bg-green-600">Active</Badge>;
      case PropertyStatus.PENDING:
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">Pending</Badge>;
      case PropertyStatus.INACTIVE:
        return <Badge className="bg-gray-500 hover:bg-gray-600">Inactive</Badge>;
      case PropertyStatus.REJECTED:
        return <Badge className="bg-red-500 hover:bg-red-600">Rejected</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };
  
  // Get counts for each status
  const statusCounts = {
    all: properties.length,
    [PropertyStatus.ACTIVE]: properties.filter((p: any) => p.status === PropertyStatus.ACTIVE).length,
    [PropertyStatus.PENDING]: properties.filter((p: any) => p.status === PropertyStatus.PENDING).length,
    [PropertyStatus.INACTIVE]: properties.filter((p: any) => p.status === PropertyStatus.INACTIVE).length,
    [PropertyStatus.REJECTED]: properties.filter((p: any) => p.status === PropertyStatus.REJECTED).length,
  };

  return (
    <AdminLayout>
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Properties Management</h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1.5">Review, approve or reject property listings</p>
      </div>
      
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search properties..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="w-full sm:w-auto">
            <Select 
              value={statusFilter} 
              onValueChange={setStatusFilter}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties ({statusCounts.all})</SelectItem>
                <SelectItem value={PropertyStatus.PENDING}>Pending ({statusCounts[PropertyStatus.PENDING]})</SelectItem>
                <SelectItem value={PropertyStatus.ACTIVE}>Active ({statusCounts[PropertyStatus.ACTIVE]})</SelectItem>
                <SelectItem value={PropertyStatus.REJECTED}>Rejected ({statusCounts[PropertyStatus.REJECTED]})</SelectItem>
                <SelectItem value={PropertyStatus.INACTIVE}>Inactive ({statusCounts[PropertyStatus.INACTIVE]})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <Tabs defaultValue="all" onValueChange={setStatusFilter} value={statusFilter}>
          <TabsList className="grid grid-cols-4 mb-4 overflow-hidden px-0 py-1 w-full">
            <TabsTrigger value="all" className="relative text-xs sm:text-sm h-10 px-1 sm:px-2 flex flex-col sm:flex-row items-center justify-center">
              <span>All</span>
              <span className="text-xs mt-0.5 sm:mt-0 sm:ml-1">({statusCounts.all})</span>
            </TabsTrigger>
            <TabsTrigger value={PropertyStatus.PENDING} className="relative text-xs sm:text-sm h-10 px-1 sm:px-2 flex flex-col sm:flex-row items-center justify-center">
              <div className="flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                <span className="hidden xs:inline">Pending</span>
                <span className="inline xs:hidden">Pend</span>
              </div>
              <span className="text-xs mt-0.5 sm:mt-0 sm:ml-1">({statusCounts[PropertyStatus.PENDING]})</span>
            </TabsTrigger>
            <TabsTrigger value={PropertyStatus.ACTIVE} className="relative text-xs sm:text-sm h-10 px-1 sm:px-2 flex flex-col sm:flex-row items-center justify-center">
              <div className="flex items-center">
                <CheckCircle className="w-3 h-3 mr-1" />
                <span className="hidden xs:inline">Active</span>
                <span className="inline xs:hidden">Act</span>
              </div>
              <span className="text-xs mt-0.5 sm:mt-0 sm:ml-1">({statusCounts[PropertyStatus.ACTIVE]})</span>
            </TabsTrigger>
            <TabsTrigger value={PropertyStatus.REJECTED} className="relative text-xs sm:text-sm h-10 px-1 sm:px-2 flex flex-col sm:flex-row items-center justify-center">
              <div className="flex items-center">
                <XCircle className="w-3 h-3 mr-1" />
                <span className="hidden xs:inline">Rejected</span>
                <span className="inline xs:hidden">Rej</span>
              </div>
              <span className="text-xs mt-0.5 sm:mt-0 sm:ml-1">({statusCounts[PropertyStatus.REJECTED]})</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" aria-label="Loading" />
        </div>
      ) : filteredProperties.length > 0 ? (
        <div>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property Code</TableHead>
                  <TableHead>Area</TableHead>
                  <TableHead>Owner Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProperties.map((property: any) => (
                  <TableRow key={property.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded overflow-hidden bg-gray-100">
                          {property.images && property.images.length > 0 ? (
                            <img 
                              src={property.images.find((img: any) => img.isPrimary)?.imageUrl || property.images[0].imageUrl} 
                              alt={property.title} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{property.title}</span>
                          </div>
                          <div 
                            className="text-xs text-muted-foreground flex items-center gap-1 bg-gray-50 px-2 py-0.5 rounded hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              navigator.clipboard.writeText(`P-${property.id.toString().padStart(6, '0')}`);
                              toast({
                                title: "Code Copied",
                                description: "Property code copied to clipboard",
                              });
                            }}
                            title="Click to copy property code"
                          >
                            <span>P-{property.id.toString().padStart(6, '0')}</span>
                            <Copy className="h-3 w-3" />
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{property.area}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-3 w-3 text-primary" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{property.ownerName || `Owner #${property.ownerId}`}</span>
                          <span className="text-xs text-muted-foreground">{property.ownerEmail || 'No email available'}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(property.status)}</TableCell>
                    <TableCell>{new Date(property.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {/* View Button - always visible */}
                        <Button 
                          variant="ghost"
                          size="icon"
                          title="View property details"
                          onClick={() => window.location.href = `/admin/properties/${property.id}`}
                          className="bg-blue-50 hover:bg-blue-100"
                        >
                          <Eye className="h-4 w-4 text-blue-600" />
                        </Button>
                        
                        {/* Edit Button - always visible */}
                        <Button 
                          variant="ghost"
                          size="icon"
                          title="Edit property"
                          onClick={() => window.location.href = `/admin/properties/${property.id}/edit`}
                          className="bg-gray-50 hover:bg-gray-100"
                        >
                          <Pencil className="h-4 w-4 text-gray-600" />
                        </Button>
                        
                        {/* Approve Button - only for pending properties */}
                        {property.status === PropertyStatus.PENDING && (
                          <Button 
                            variant="ghost"
                            size="icon"
                            title="Approve property"
                            onClick={() => handleApprove(property.id)}
                            className="bg-green-50 hover:bg-green-100"
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                        )}
                        
                        {/* Reject Button - only for pending properties */}
                        {property.status === PropertyStatus.PENDING && (
                          <Button 
                            variant="ghost"
                            size="icon"
                            title="Reject property"
                            onClick={() => handleRejectClick(property)}
                            className="bg-red-50 hover:bg-red-100"
                          >
                            <X className="h-4 w-4 text-red-600" />
                          </Button>
                        )}
                        
                        {/* Delete Button - always visible */}
                        <Button 
                          variant="ghost"
                          size="icon"
                          title="Delete property"
                          onClick={() => {
                            if (confirm(`Are you sure you want to delete "${property.title}"? This action cannot be undone.`)) {
                              apiRequest('DELETE', `properties/${property.id}`)
                                .then(() => {
                                  toast({
                                    title: "Property Deleted",
                                    description: "The property has been permanently removed from the system.",
                                  });
                                  queryClient.invalidateQueries({ queryKey: ['admin/properties'] });
                                })
                                .catch(error => {
                                  console.error('Error deleting property:', error);
                                  toast({
                                    title: "Error",
                                    description: "Failed to delete property. Please try again.",
                                    variant: "destructive",
                                  });
                                });
                            }
                          }}
                          className="bg-red-50 hover:bg-red-100"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {filteredProperties.map((property: any) => (
              <div key={property.id} className="relative border rounded-lg overflow-hidden bg-white">
                <div className="p-3">
                  <div className="flex items-start mb-2">
                    <div className="relative w-14 h-14 rounded-md overflow-hidden bg-gray-100 mr-3">
                      {property.images && property.images.length > 0 ? (
                        <img 
                          src={property.images.find((img: any) => img.isPrimary)?.imageUrl || property.images[0].imageUrl} 
                          alt={property.title} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col">
                        <div className="flex justify-between items-start">
                          <h3 className="font-semibold text-sm">{property.title}</h3>
                          <div className="ml-2">{getStatusBadge(property.status)}</div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 capitalize">
                          {property.area}, Dubai
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          P-{property.id.toString().padStart(6, '0')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex border-t">
                  <Link href={`/admin/properties/${property.id}`} className="flex-1 py-2 text-center text-xs border-r text-blue-600">
                    <Eye className="inline-block h-3.5 w-3.5 mr-1" />
                    View
                  </Link>
                  
                  <Link href={`/admin/properties/${property.id}/edit`} className="flex-1 py-2 text-center text-xs text-gray-600">
                    <Pencil className="inline-block h-3.5 w-3.5 mr-1" />
                    Edit
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-16 bg-muted/30 rounded-lg">
          <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-bold mb-2">No properties found</h3>
          <p className="text-muted-foreground mb-6">No properties match your current search and filters</p>
          <Button variant="outline" onClick={() => {
            setSearchQuery('');
            setStatusFilter('all');
          }}>Clear Filters</Button>
        </div>
      )}
      
      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Property</DialogTitle>
            <DialogDescription>
              Provide a reason for rejection. This will be visible to the property owner.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded overflow-hidden bg-muted shrink-0">
                {propertyToReject?.images && propertyToReject.images.length > 0 ? (
                  <img 
                    src={propertyToReject.images.find((img: any) => img.isPrimary)?.imageUrl || propertyToReject.images[0].imageUrl} 
                    alt={propertyToReject.title} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
              </div>
              <div>
                <h4 className="font-semibold">{propertyToReject?.title}</h4>
                <p className="text-sm text-muted-foreground">
                  {propertyToReject?.area}, {propertyToReject?.address}
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Rejection Reason</label>
              <Textarea 
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this property is being rejected..."
                className="min-h-[120px]"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRejectConfirm} 
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending ? "Rejecting..." : "Reject Property"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}